import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDays, Users, Clock } from 'lucide-react';

const MENUS = [
  {
    name: 'Menú de Temporada',
    desc: 'Un recorrido por los sabores de la estación',
    items: ['Ensalada de vieiras con cítricos', 'Ravioli de trufa negra', 'Lubina al horno con hierbas', 'Tarta tatin de manzana', 'Petit fours y mignardises'],
    price: '95€',
    priceNote: 'por persona · 5 platos',
  },
  {
    name: 'Menú Degustación',
    desc: 'La experiencia completa del Chef Moreau. Ocho platos que cuentan una historia.',
    items: ['Amuse-bouche del chef', 'Tartar de langostino', 'Foie gras mi-cuit', 'Risotto de setas silvestres', 'Rodaballo con beurre blanc', 'Cordero lechal confitado', 'Selección de quesos', 'Soufflé de chocolate Valrhona'],
    price: '185€',
    priceNote: 'por persona · 8 platos',
    featured: true,
  },
  {
    name: 'A la Carta',
    desc: 'Disponible en servicio de cena. Selección del chef actualizada semanalmente.',
    items: ['Entrantes desde 28€', 'Principales desde 42€', 'Postres desde 18€', 'Maridaje de vinos desde 45€'],
    price: 'Desde 88€',
    priceNote: 'por persona',
  },
];

const DINNER_SLOTS = ['20:00', '20:30', '21:00', '21:30', '22:00'];
const LUNCH_SLOTS = ['13:00', '13:30', '14:00', '14:30'];

function sanitizeInput(str) {
  if (typeof str !== 'string') return '';
  return str.trim().replace(/[<>'"]/g, '').slice(0, 500);
}

export default function RestaurantPage() {
  const [tableDate, setTableDate] = useState(null);
  const [tableTime, setTableTime] = useState(null);
  const [tableGuests, setTableGuests] = useState(2);
  const [tableService, setTableService] = useState('dinner');
  const [tableName, setTableName] = useState('');
  const [tableEmail, setTableEmail] = useState('');
  const [tablePhone, setTablePhone] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  const slots = tableService === 'lunch' ? LUNCH_SLOTS : DINNER_SLOTS;

  const showError = useCallback((msg) => {
    setError(msg);
    setTimeout(() => setError(''), 4000);
  }, []);

  const confirmBooking = () => {
    const name = sanitizeInput(tableName);
    const email = sanitizeInput(tableEmail);
    const phone = sanitizeInput(tablePhone);
    if (!tableDate || !tableTime) return showError('Selecciona fecha y hora');
    if (!name || !email || !phone) return showError('Por favor rellena todos los datos');

    const booking = {
      id: 'LJ' + Date.now().toString().slice(-6),
      date: format(tableDate, 'dd/MM/yyyy'),
      time: tableTime,
      guests: tableGuests,
      service: tableService,
      guest: { name, email, phone },
      timestamp: new Date().toISOString(),
    };

    try {
      const existing = JSON.parse(localStorage.getItem('tableBookings') || '[]');
      existing.push(booking);
      localStorage.setItem('tableBookings', JSON.stringify(existing));
    } catch(e) { /* */ }

    setConfirmation(booking);
  };

  return (
    <div className="restaurant-page" data-testid="restaurant-page">
      {/* Hero */}
      <section className="restaurant-hero" data-testid="restaurant-hero">
        <img className="restaurant-hero__bg" src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80" alt="Le Jardin restaurant" />
        <div className="restaurant-hero__overlay" />
        <div className="restaurant-hero__content">
          <div className="gold-line reveal" style={{ marginBottom: '1rem' }} />
          <h1 className="restaurant-hero__title reveal stagger-1">Le Jardin</h1>
          <p className="restaurant-hero__sub reveal stagger-2">Restaurante gastronómico · Chef Antoine Moreau</p>
        </div>
      </section>

      {/* Info bar */}
      <section style={{ background: 'var(--carbon)', borderBottom: '1px solid var(--gold-border)', padding: '1.5rem 0' }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            <Clock size={16} style={{ color: 'var(--gold)' }} />
            <span>Almuerzo: L-V 13:00–15:30</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            <Clock size={16} style={{ color: 'var(--gold)' }} />
            <span>Cena: Ma-Do 20:00–22:30</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontFamily: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
            <Users size={16} style={{ color: 'var(--gold)' }} />
            <span>11 mesas · Reserva recomendada</span>
          </div>
        </div>
      </section>

      {/* Menus */}
      <section className="menu-section" data-testid="menu-section">
        <div className="container">
          <div className="section-header reveal">
            <div className="gold-line" />
            <span className="section-label">La Carte</span>
            <h2 className="section-title">Nuestra propuesta</h2>
          </div>
          <div className="menu-section__grid">
            {MENUS.map((menu, i) => (
              <div key={i} className={`menu-card ${menu.featured ? 'menu-card--featured' : ''} reveal stagger-${i + 1}`} data-testid={`menu-card-${i}`}>
                <h3 className="menu-card__name">{menu.name}</h3>
                <p className="menu-card__desc">{menu.desc}</p>
                <ul className="menu-card__items">
                  {menu.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
                <div className="menu-card__price">{menu.price}</div>
                <div className="menu-card__price-note">{menu.priceNote}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Table Reservation */}
      <section className="table-reservation" id="reservar" data-testid="table-reservation">
        <div className="table-reservation__inner">
          <div className="section-header reveal">
            <div className="gold-line" />
            <span className="section-label">Réservation</span>
            <h2 className="section-title">Reservar mesa</h2>
          </div>

          {error && <div className="booking-toast booking-toast--error">{error}</div>}

          <div className="table-reservation__form">
            <div className="booking-field">
              <label>Servicio</label>
              <select value={tableService} onChange={e => { setTableService(e.target.value); setTableTime(null); }} data-testid="table-service">
                <option value="dinner">Cena</option>
                <option value="lunch">Almuerzo</option>
              </select>
            </div>
            <div className="booking-field">
              <label>Comensales</label>
              <select value={tableGuests} onChange={e => setTableGuests(Number(e.target.value))} data-testid="table-guests">
                {[1,2,3,4,5,6,7,8].map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? 'persona' : 'personas'}</option>
                ))}
              </select>
            </div>
            <div className="booking-field">
              <label>Fecha</label>
              <Popover open={dateOpen} onOpenChange={setDateOpen}>
                <PopoverTrigger asChild>
                  <button type="button" style={{ width: '100%', padding: '0.85rem 1rem', background: 'var(--onyx)', border: '1px solid rgba(201,169,110,0.15)', color: tableDate ? 'var(--cream)' : 'var(--text-muted)', fontFamily: 'var(--font-ui)', fontSize: 'var(--text-sm)', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} data-testid="table-date-btn">
                    {tableDate ? format(tableDate, 'dd / MM / yyyy') : 'Seleccionar fecha'}
                    <CalendarDays size={16} style={{ opacity: 0.5 }} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" style={{ background: 'var(--obsidian)', border: '1px solid var(--gold-border)' }}>
                  <Calendar
                    mode="single"
                    selected={tableDate}
                    onSelect={(d) => { setTableDate(d); setDateOpen(false); }}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    locale={es}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="booking-field">
              <label>Hora</label>
              <div className="time-slots" data-testid="time-slots">
                {slots.map(time => (
                  <button key={time} type="button" className={`time-slot ${tableTime === time ? 'selected' : ''}`} onClick={() => setTableTime(time)} data-testid={`time-slot-${time}`}>
                    {time}
                  </button>
                ))}
              </div>
            </div>
            <div className="booking-field">
              <label>Nombre *</label>
              <input type="text" value={tableName} onChange={e => setTableName(e.target.value)} placeholder="Tu nombre" data-testid="table-name" />
            </div>
            <div className="booking-field">
              <label>Email *</label>
              <input type="email" value={tableEmail} onChange={e => setTableEmail(e.target.value)} placeholder="email@ejemplo.com" data-testid="table-email" />
            </div>
            <div className="booking-field">
              <label>Teléfono *</label>
              <input type="tel" value={tablePhone} onChange={e => setTablePhone(e.target.value)} placeholder="+34 600 000 000" data-testid="table-phone" />
            </div>
            <div className="booking-field" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button className="btn-gold" style={{ width: '100%', justifyContent: 'center' }} onClick={confirmBooking} data-testid="table-confirm-btn">
                Confirmar reserva
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '4rem 0', background: 'var(--void)', textAlign: 'center' }}>
        <div className="container reveal">
          <p style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-xl)', fontStyle: 'italic', color: 'var(--text-secondary)', marginBottom: '2rem' }}>
            "The best way to experience Le Jardin is to let Chef Moreau surprise you."
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contacto" className="btn-outline-gold" data-testid="restaurant-contact-link">Contactar</Link>
            <a href="https://wa.me/34600000000?text=Hola,%20quiero%20reservar%20mesa%20en%20Le%20Jardin" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="restaurant-whatsapp-link">
              Reservar por WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Confirmation Modal */}
      {confirmation && (
        <div className="modal-overlay" data-testid="table-confirmation-modal">
          <div className="modal">
            <div className="modal__icon">&#10022;</div>
            <h3 className="modal__title">Mesa reservada</h3>
            <p className="modal__subtitle">Merci, {confirmation.guest.name.split(' ')[0]}.</p>
            <div className="modal__details">
              <div>Nº Reserva: <strong data-testid="table-conf-id">{confirmation.id}</strong></div>
              <div>Fecha: <strong>{confirmation.date}</strong></div>
              <div>Hora: <strong>{confirmation.time}</strong></div>
              <div>Comensales: <strong>{confirmation.guests}</strong></div>
            </div>
            <p className="modal__note">
              <em>Demo: En producción recibirías un email de confirmación en {confirmation.guest.email}</em>
            </p>
            <button onClick={() => setConfirmation(null)} className="btn-gold" style={{ marginTop: '1.5rem' }} data-testid="table-conf-close">
              Cerrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
